<template>
  <el-drawer
    custom-class="bottom_drop_up applicant-actions-sheet"
    :model-value="modelValue"
    :append-to-body="true"
    :with-header="false"
    :direction="'btt'"
    :destroy-on-close="true"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="sheet-handle" aria-hidden="true"></div>

    <div class="sheet-header">
      <el-avatar v-if="locum?.image" :size="72" shape="circle" :src="locum.image" />
      <div v-else class="sheet-initials">{{ initials }}</div>
      <div class="sheet-name">{{ displayName }}</div>
      <div v-if="rateLabel" class="sheet-rate">Locum rate: {{ rateLabel }}</div>
    </div>

    <div v-if="ahpra.number" class="sheet-card">
      <div class="sheet-card-icon sheet-card-icon--ahpra" aria-hidden="true">
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="11" cy="11" r="11" fill="#E8F1FF" />
          <path d="M6.5 11.2L9.3 14L15.5 7.8" stroke="#1D4ED8" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </div>
      <div class="sheet-card-main">
        <div class="sheet-card-label">AHPRA registration</div>
        <div class="sheet-card-value">{{ ahpra.number }}</div>
      </div>
      <div v-if="ahpraExpiry" class="sheet-card-aside">
        <div class="sheet-card-label">Expires</div>
        <div class="sheet-card-value">{{ ahpraExpiry }}</div>
      </div>
    </div>

    <div v-for="locumCv in cvs" :key="locumCv.url || locumCv.id" class="sheet-card">
      <div class="sheet-card-icon sheet-card-icon--file" aria-hidden="true">
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7 3.5H16.2L21.5 8.8V24.5H7V3.5Z" fill="#FDECEC" stroke="#E24B4B" stroke-width="1.2" />
          <path d="M16 3.5V9H21.5" stroke="#E24B4B" stroke-width="1.2" />
          <path d="M10 16.5H18.5" stroke="#E24B4B" stroke-width="1.2" stroke-linecap="round" />
          <path d="M10 19.5H15.5" stroke="#E24B4B" stroke-width="1.2" stroke-linecap="round" />
        </svg>
      </div>
      <div class="sheet-card-main">
        <div class="sheet-card-value">{{ locumCv.fileName || locumCv.userFriendlyName || "CV" }}</div>
        <div class="sheet-card-meta">{{ fileMeta(locumCv) }}</div>
      </div>
      <button type="button" class="sheet-download" aria-label="Download CV" @click="openCv(locumCv)">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 3V14" stroke="#1A1A1A" stroke-width="1.6" stroke-linecap="round" />
          <path d="M8 11L12 15L16 11" stroke="#1A1A1A" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M5 19H19" stroke="#1A1A1A" stroke-width="1.6" stroke-linecap="round" />
        </svg>
      </button>
    </div>

    <div v-if="showPreferences" class="preference-row">
      <template v-if="!isBlackListed">
        <el-checkbox
          v-show="!isPreferred && !isInvitationPending"
          v-model="preferChecked"
          :disabled="preferenceBusy"
          @change="addToPreferredLocum"
        >
          Set as preferred locum
        </el-checkbox>
        <p v-show="isInvitationPending" class="preference-status">Invited to become preferred locum</p>
        <el-checkbox
          v-show="isPreferred && !isInvitationPending"
          v-model="removePreferredChecked"
          :disabled="preferenceBusy"
          @change="removeAsPreferredLocum"
        >
          Remove as preferred locum
        </el-checkbox>
      </template>
      <template v-if="!isPreferred && !isInvitationPending">
        <el-checkbox
          v-show="!isBlackListed"
          v-model="blockChecked"
          :disabled="preferenceBusy"
          @change="addToBlackList"
        >
          Block locum
        </el-checkbox>
        <el-checkbox
          v-show="isBlackListed"
          v-model="unblockChecked"
          :disabled="preferenceBusy"
          @change="removeAsBlackListed"
        >
          Unblock locum
        </el-checkbox>
      </template>
    </div>

    <button v-if="showChat" type="button" class="sheet-btn sheet-btn--chat" @click="openChat">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M8.5 19H8C4 19 2 18 2 13V8C2 4 4 2 8 2H16C20 2 22 4 22 8V13C22 17 20 19 16 19H15.5C15.19 19 14.89 19.15 14.7 19.4L13.2 21.4C12.54 22.28 11.46 22.28 10.8 21.4L9.3 19.4C9.14 19.18 8.77 19 8.5 19Z"
          stroke="#1A1A1A"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path d="M8 11H8.01" stroke="#1A1A1A" stroke-width="2" stroke-linecap="round" />
        <path d="M12 11H12.01" stroke="#1A1A1A" stroke-width="2" stroke-linecap="round" />
        <path d="M16 11H16.01" stroke="#1A1A1A" stroke-width="2" stroke-linecap="round" />
      </svg>
      <span>Chat with {{ firstName }}</span>
    </button>

    <div v-if="showShiftActions" class="sheet-actions">
      <button type="button" class="sheet-btn sheet-btn--decline" @click="onDecline">Decline</button>
      <button type="button" class="sheet-btn sheet-btn--offer" @click="onOffer">Offer shift</button>
    </div>
  </el-drawer>
</template>

<script setup>
import { computed, defineEmits, defineProps, ref, watch } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { ElMessage, ElMessageBox } from "@/components/Vendor/Element";
import { formatAhpraDate, resolveAhpra } from "@/helpers/ahpra/ahpraDisplay.js";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  locum: {
    type: Object,
    default: null,
  },
  locumRate: {
    type: [Number, String],
    default: null,
  },
  showShiftActions: {
    type: Boolean,
    default: true,
  },
  showPreferences: {
    type: Boolean,
    default: true,
  },
  showChat: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["update:modelValue", "offer", "decline", "chat"]);

const store = useStore();
const router = useRouter();
const preferenceBusy = ref(false);
const preferredRecords = ref(null);
const invitationRecords = ref(null);
const preferChecked = ref(false);
const removePreferredChecked = ref(false);
const blockChecked = ref(false);
const unblockChecked = ref(false);

function resetPreferenceChecks() {
  preferChecked.value = false;
  removePreferredChecked.value = false;
  blockChecked.value = false;
  unblockChecked.value = false;
}

const userData = computed(() => store.getters.GET_USER_DATA);
const locumId = computed(() => props.locum?.id);
const ahpra = computed(() => resolveAhpra(props.locum));
const ahpraExpiry = computed(() => formatAhpraDate(ahpra.value.date));
const cvs = computed(() => props.locum?.locumCvs || []);

const displayName = computed(() => {
  const locum = props.locum;
  if (!locum) {
    return "Unknown locum";
  }
  const first = (locum.name || "").trim();
  const last = (locum.lastName || locum.last_name || "").trim();
  const full = !last || first.toLowerCase().endsWith(last.toLowerCase())
    ? first
    : `${first} ${last}`.trim();
  return full || "Unknown locum";
});

const initials = computed(() => {
  const parts = displayName.value.split(/\s+/).filter((part) => part && part !== "Unknown");
  if (!parts.length || displayName.value === "Unknown locum") {
    return "?";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
});

const firstName = computed(() => {
  const name = (props.locum?.name || "").trim();
  return name ? name.split(/\s+/)[0] : "locum";
});

const rateLabel = computed(() => {
  if (props.locumRate === null || props.locumRate === undefined || props.locumRate === "") {
    return "";
  }
  const amount = Number(props.locumRate);
  if (!Number.isFinite(amount)) {
    return "";
  }
  const formatted = new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
  }).format(amount);
  return `${formatted}/hr`;
});

function sameId(left, right) {
  return left != null && right != null && String(left) === String(right);
}

function recordLocumId(item) {
  return item?.locumId ?? item?.locum_id ?? item?.locum?.id ?? null;
}

function invitationAccepted(item) {
  const acceptedAt = item?.acceptedAt ?? item?.accepted_at;
  return acceptedAt != null && acceptedAt !== "";
}

const preferredList = computed(() => preferredRecords.value ?? userData.value?.preferredLocum ?? []);
const invitationList = computed(() => invitationRecords.value ?? userData.value?.preferredLocumInvitations ?? []);

const locumInvitations = computed(() => {
  const id = locumId.value;
  if (!id) {
    return [];
  }
  return invitationList.value.filter((item) => sameId(recordLocumId(item), id));
});

const isPreferred = computed(() => {
  const id = locumId.value;
  if (!id) {
    return false;
  }
  const onPreferredList = preferredList.value.some((item) => {
    const removedAt = item?.removeAt ?? item?.remove_at;
    return sameId(recordLocumId(item), id) && (removedAt == null || removedAt === "");
  });
  return onPreferredList || locumInvitations.value.some((item) => invitationAccepted(item));
});

const isInvitationPending = computed(() => {
  if (isPreferred.value) {
    return false;
  }
  return locumInvitations.value.some((item) => !invitationAccepted(item));
});

async function loadPreferenceStatus() {
  if (!props.showPreferences) {
    return;
  }
  const [preferredResult, invitationResult] = await Promise.allSettled([
    store.dispatch("FETCH_PREFERRED_LOCUM"),
    store.dispatch("FETCH_PREFERRED_LOCUM_FOR_PHARMACY"),
  ]);
  if (preferredResult.status === "fulfilled" && Array.isArray(preferredResult.value)) {
    preferredRecords.value = preferredResult.value;
  }
  if (invitationResult.status === "fulfilled" && Array.isArray(invitationResult.value)) {
    invitationRecords.value = invitationResult.value;
  }
}

watch(() => props.modelValue, (open) => {
  if (open) {
    resetPreferenceChecks();
    loadPreferenceStatus();
  }
});

const isBlackListed = computed(() => {
  const id = locumId.value;
  if (!id) {
    return false;
  }
  return (userData.value?.blacklistedLocum || []).some((item) => String(item.locumId) === String(id));
});

function fileMeta(file) {
  const extension = String(file.extension || "pdf").replace(/^\./, "").toUpperCase();
  const sizeLabel = formatFileSize(file.size);
  return sizeLabel ? `${extension} • ${sizeLabel}` : extension;
}

function formatFileSize(size) {
  const value = Number(size);
  if (!Number.isFinite(value)) {
    return size ? String(size) : "";
  }
  const megabytes = value > 1024 ? value / (1024 * 1024) : value;
  return `${megabytes.toFixed(2)} MB`;
}

function openCv(file) {
  if (file?.url) {
    window.open(file.url, "_blank");
  }
}

function closeSheet() {
  emit("update:modelValue", false);
}

function onOffer() {
  closeSheet();
  emit("offer");
}

function onDecline() {
  closeSheet();
  emit("decline");
}

function openChat() {
  const user = userData.value;
  if (!user?.id || !props.locum?.id) {
    return;
  }
  const chatId = user.role === "pharmacy"
    ? `${user.id}.${props.locum.id}`
    : `${props.locum.id}.${user.id}`;
  closeSheet();
  emit("chat");
  router.push({
    name: "chat_message",
    params: { chatId },
  });
}

async function confirmAction(message, confirmButtonClass) {
  try {
    await ElMessageBox.confirm(message, "Confirmation", {
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      confirmButtonClass,
      type: "warning",
    });
    return true;
  } catch (error) {
    return false;
  }
}

async function addToPreferredLocum(checked) {
  if (!checked || preferenceBusy.value || !locumId.value) {
    preferChecked.value = false;
    return;
  }
  const name = props.locum?.name || "this locum";
  const confirmed = await confirmAction(
    `Are you sure you want to add ${name} as preferred?`,
    "el-button--blue"
  );
  if (!confirmed) {
    preferChecked.value = false;
    return;
  }
  preferenceBusy.value = true;
  try {
    await store.dispatch("ADD_PREFERRED_LOCUM_FROM_PROFILE", {
      type: "add",
      locum_id: locumId.value,
    });
    await loadPreferenceStatus();
    const invited = isInvitationPending.value;
    ElMessage({
      type: "success",
      message: invited
        ? `An invitation has been sent to ${name}. Once accepted, ${name} will be added as a preferred locum.`
        : `${name} is added as preferred locum`,
    });
  } catch (error) {
    const message = typeof error?.message === "string" ? error.message : "Could not update this locum";
    ElMessage({ type: "error", message });
  } finally {
    preferenceBusy.value = false;
    preferChecked.value = false;
  }
}

async function removeAsPreferredLocum(checked) {
  if (!checked || preferenceBusy.value || !locumId.value) {
    removePreferredChecked.value = false;
    return;
  }
  const name = props.locum?.name || "this locum";
  const confirmed = await confirmAction(
    `Are you sure you want to remove ${name} as preferred?`,
    "el-button--red"
  );
  if (!confirmed) {
    removePreferredChecked.value = false;
    return;
  }
  preferenceBusy.value = true;
  try {
    await store.dispatch("ADD_PREFERRED_LOCUM_FROM_PROFILE", {
      type: "remove",
      locum_id: locumId.value,
    });
    await loadPreferenceStatus();
    ElMessage({
      type: "success",
      message: "Locum is removed from preferred locum",
    });
  } catch (error) {
    const message = typeof error?.message === "string" ? error.message : "Could not update this locum";
    ElMessage({ type: "error", message });
  } finally {
    preferenceBusy.value = false;
    removePreferredChecked.value = false;
  }
}

async function addToBlackList(checked) {
  if (!checked || preferenceBusy.value || !locumId.value) {
    blockChecked.value = false;
    return;
  }
  const name = props.locum?.name || "this locum";
  const confirmed = await confirmAction(
    `Are you sure you want to add ${name} to blacklist?`,
    "el-button--blue"
  );
  if (!confirmed) {
    blockChecked.value = false;
    return;
  }
  preferenceBusy.value = true;
  try {
    await store.dispatch("ADD_LOCUM_TO_BLACKLIST_FROM_PROFILE", {
      type: "add",
      locum_id: locumId.value,
    });
    await loadPreferenceStatus();
    ElMessage({
      type: "success",
      message: `${name} is added as blacklisted`,
    });
  } catch (error) {
    const message = typeof error?.message === "string" ? error.message : "Could not update this locum";
    ElMessage({ type: "error", message });
  } finally {
    preferenceBusy.value = false;
    blockChecked.value = false;
  }
}

async function removeAsBlackListed(checked) {
  if (!checked || preferenceBusy.value || !locumId.value) {
    unblockChecked.value = false;
    return;
  }
  const name = props.locum?.name || "this locum";
  const confirmed = await confirmAction(
    `Are you sure you want to remove ${name} from blacklist?`,
    "el-button--red"
  );
  if (!confirmed) {
    unblockChecked.value = false;
    return;
  }
  preferenceBusy.value = true;
  try {
    await store.dispatch("ADD_LOCUM_TO_BLACKLIST_FROM_PROFILE", {
      type: "remove",
      locum_id: locumId.value,
    });
    await loadPreferenceStatus();
    ElMessage({
      type: "success",
      message: `${name} is removed from blacklist`,
    });
  } catch (error) {
    const message = typeof error?.message === "string" ? error.message : "Could not update this locum";
    ElMessage({ type: "error", message });
  } finally {
    preferenceBusy.value = false;
    unblockChecked.value = false;
  }
}
</script>

<style lang="scss">
.el-drawer.applicant-actions-sheet {
  .el-drawer__body {
    text-align: center;
    padding-top: 8px;
  }

  .sheet-handle {
    width: 42px;
    height: 4px;
    margin: 0 auto 16px;
    border-radius: 999px;
    background: #d7dde6;
  }

  .sheet-header {
    margin-bottom: 20px;
  }

  .sheet-initials {
    width: 72px;
    height: 72px;
    margin: 0 auto;
    border-radius: 50%;
    background: #eef2f6;
    color: #16324f;
    font-size: 22px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .el-avatar {
    margin-bottom: 0;
  }

  .sheet-name {
    margin-top: 14px;
    color: #16324f;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.25;
  }

  .sheet-rate {
    margin-top: 4px;
    color: #8b95a5;
    font-size: 15px;
  }

  .sheet-card {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
    padding: 14px 16px;
    text-align: left;
    background: #fff;
    border: 1px solid #e6ebf2;
    border-radius: 16px;
  }

  .sheet-card-main {
    min-width: 0;
    flex: 1;
  }

  .sheet-card-aside {
    text-align: right;
  }

  .sheet-card-label {
    color: #8b95a5;
    font-size: 13px;
  }

  .sheet-card-value {
    color: #16324f;
    font-size: 15px;
    font-weight: 700;
    word-break: break-word;
  }

  .sheet-card-meta {
    margin-top: 2px;
    color: #8b95a5;
    font-size: 13px;
  }

  .sheet-download {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
  }

  .preference-row {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: 10px 30px;
    margin: 4px 11px 16px;
    text-align: left;

    .el-checkbox {
      height: auto;
      margin: 0;
      color: #16324f;
      font-weight: 400;
    }

    .el-checkbox__label {
      color: #16324f;
      font-size: 14px;
      font-weight: 400;
      line-height: 1.2;
    }
  }

  .preference-status {
    margin: 0;
    color: #16324f;
    font-size: 14px;
  }

  .sheet-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    min-height: 48px;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
  }

  .sheet-btn--chat {
    margin-bottom: 12px;
    color: #16324f;
    background: #fff;
    border: 1px solid #e1e6ee;
  }

  .sheet-actions {
    display: flex;
    gap: 12px;
  }

  .sheet-btn--decline {
    color: #e24b4b;
    background: #fff;
    border: 1px solid #f0b4b4;
  }

  .sheet-btn--offer {
    color: #fff;
    background: rgb(var(--locumate-primary-color));
    border: 1px solid rgb(var(--locumate-primary-color));
  }
}
</style>
